import * as React from 'react'
import { useCallback, useMemo } from 'react'

import {
  useDeleteResourcesCustomFilterMutation,
  useGetResourcesCustomFiltersByProjectQuery,
  useSaveResourcesCustomFilterMutation,
} from '~/client/graph/operations/generated/CustomResourcesFilters.generated'
import {
  DeleteFilterMutationFn,
  FilterTypeDescriptor,
  SavedViews,
  SaveFilterMutationFn,
} from '~/client/src/desktop/components/SavedViews'

import { useResource } from '../context'

interface ResourceSavedViewsProps {
  projectId: string
}

export const ResourceSavedViews: React.FC<ResourceSavedViewsProps> = ({
  projectId,
}) => {
  const {
    state: { appliedFilters, grouping },
    actions: { setAppliedFilters, setGrouping },
    meta: { facets, groupingOptions, resourceTypeId, getOptionName },
  } = useResource()

  const savedViewFacets = useMemo(
    (): FilterTypeDescriptor[] =>
      facets.map(facet => ({
        key: facet.key,
        label: facet.label,
        renderFilterValue: (value: any): JSX.Element | null => {
          const displayName = getOptionName(String(value), facet.key)
          return React.createElement(
            'div',
            {
              className:
                'tw:inline-flex tw:text-body-sm tw:text-foreground tw:overflow-hidden tw:whitespace-nowrap tw:text-ellipsis tw:max-w-[270px]',
            },
            displayName,
          )
        },
      })),
    [facets, getOptionName],
  )

  const { data: savedViewsData } = useGetResourcesCustomFiltersByProjectQuery({
    variables: { projectId, resourceTypeId },
    skip: !projectId || !resourceTypeId,
  })

  const savedViews = useMemo(() => {
    return (
      savedViewsData?.resourcesCustomFilters?.data?.map(filter => ({
        id: filter.id,
        name: filter.name,
        isPublic: filter.isPublic,
        filtersByFilterType: filter.filtersByFilterType,
        groupBy: filter.groupBy,
        userId: filter.userId,
      })) || []
    )
  }, [savedViewsData])

  const [saveResourcesCustomFilter] = useSaveResourcesCustomFilterMutation()
  const [deleteResourcesCustomFilter] = useDeleteResourcesCustomFilterMutation()

  const saveFilterMutation: SaveFilterMutationFn = useCallback(
    async options => {
      return saveResourcesCustomFilter({
        ...options,
        variables: {
          ...options.variables,
          filter: {
            ...options.variables?.filter,
            resourceTypeId,
          },
        },
      })
    },
    [resourceTypeId, saveResourcesCustomFilter],
  )

  const deleteFilterMutation: DeleteFilterMutationFn = useCallback(
    async options => {
      return deleteResourcesCustomFilter(options)
    },
    [deleteResourcesCustomFilter],
  )

  const handleApplyFilters = useCallback(
    (filters: { [key: string]: Set<string> }, groupBy: string) => {
      setAppliedFilters(filters)
      setGrouping(groupBy || '')
    },
    [setAppliedFilters, setGrouping],
  )

  return (
    <SavedViews
      className="tw:flex-1 tw:min-w-0"
      views={savedViews}
      onViewSelect={handleApplyFilters}
      filterType="resources"
      saveFilterMutation={saveFilterMutation}
      deleteFilterMutation={deleteFilterMutation}
      refetchQueryName="GetResourcesCustomFiltersByProject"
      appliedFilters={appliedFilters}
      currentGrouping={grouping}
      facets={savedViewFacets}
      groupingOptions={groupingOptions}
    />
  )
}
